import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { WidgetRenderer } from "./DashboardGrid";
import { VIEWS, buildWidget, needsTag } from "./buildWidget";
import {
  getDashboardDataLatest,
  getDashboardDataSeries,
  getDashboardDataStats,
  getDashboardEdgeSystem,
} from "../../api/dashboardApi";

const SOURCES = ["data", "alarm_table", "anomaly_table", "event_feed", "edge_system", "edge_logs"];
const DATA_VIEWS = ["gauge", "kpi", "linechart", "table", "statuscard"];

export default function AddWidgetDrawer({
  open,
  onClose,
  accessToken,
  edges,
  selectedEdge,
  onSelectEdge,
  devices,
  devicesError,
  deviceIds,
  setDeviceIds,
  tags,
  tagNames,
  setTagNames,
  widgetCount,
  onAdd,
}) {
  const { t } = useTranslation();
  const [stepIdx, setStepIdx] = useState(0);
  const [source, setSource] = useState("data");
  const [view, setView] = useState("gauge");
  const [range, setRange] = useState("1h");
  const [autoRange, setAutoRange] = useState(true);
  const [min, setMin] = useState("0");
  const [max, setMax] = useState("100");
  const [title, setTitle] = useState("");
  const [preview, setPreview] = useState({});
  const dialogRef = useRef(null);

  const type = source === "data" ? view : source;
  const steps = useMemo(
    () => (source === "data" ? ["source", "tag", "view", "settings"] : ["source", "view", "settings"]),
    [source]
  );
  const step = steps[Math.min(stepIdx, steps.length - 1)];

  // Home passes a fresh onClose on every render (5s refresh, data fetches); keep it in a ref so
  // the effects below only run when the drawer opens, not on each parent render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    setStepIdx(0);
    dialogRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onCloseRef.current();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const minNum = autoRange ? undefined : Number(min);
  const maxNum = autoRange ? undefined : Number(max);
  const rangeInvalid = type === "gauge" && !autoRange && !(Number.isFinite(minNum) && Number.isFinite(maxNum) && maxNum > minNum);

  const draft = useMemo(() => {
    if (!selectedEdge) return null;
    return buildWidget({
      id: "preview",
      type,
      edge: selectedEdge,
      deviceIds,
      tagNames,
      deviceNames: devices.filter((d) => deviceIds.includes(d.id)).map((d) => d.name).join(", "),
      tagLabels: tags.filter((tg) => tagNames.includes(tg.name)).map((tg) => tg.label || tg.name).join(", "),
      range,
      min: minNum,
      max: maxNum,
      title: title.trim(),
      index: widgetCount,
    });
  }, [selectedEdge, type, deviceIds, tagNames, devices, tags, range, minNum, maxNum, title, widgetCount]);

  // Live preview data: only once a data widget has its tags, or for edge_system
  const ready = open && selectedEdge && (needsTag(type) ? deviceIds.length > 0 && tagNames.length > 0 : type === "edge_system");
  const edgeId = selectedEdge?.edgeId;
  useEffect(() => {
    if (!ready || !accessToken) {
      setPreview({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        if (type === "edge_system") {
          const system = await getDashboardEdgeSystem(accessToken, edgeId);
          if (!cancelled) setPreview({ system });
          return;
        }
        const [latest, series, stats] = await Promise.all([
          getDashboardDataLatest(accessToken, edgeId, deviceIds, tagNames),
          ["kpi", "linechart", "table"].includes(type)
            ? getDashboardDataSeries(accessToken, edgeId, deviceIds, tagNames, "1h")
            : Promise.resolve(null),
          type === "gauge" ? getDashboardDataStats(accessToken, edgeId, deviceIds, tagNames, "24h").catch(() => null) : Promise.resolve(null),
        ]);
        if (!cancelled) {
          setPreview({
            latest: Array.isArray(latest) ? latest : [],
            series: series?.data || [],
            stats: stats?.data || [],
          });
        }
      } catch (_) {
        if (!cancelled) setPreview({ latest: [], series: [], stats: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, accessToken, edgeId, type, deviceIds, tagNames]);

  if (!open) return null;

  const canNext =
    step === "source" ? !!selectedEdge
    : step === "tag" ? deviceIds.length > 0 && tagNames.length > 0
    : step === "view" ? !!type
    : !rangeInvalid;
  const last = stepIdx === steps.length - 1;

  const submit = () => {
    if (!draft) return;
    onAdd({ ...draft, id: `w-${Date.now()}` });
    onClose();
  };

  const previewWidgetData = { preview: preview };
  const previewHasData = !!(preview.latest || preview.system);

  return (
    <div style={s.scrim} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={t("home.drawer.title")} style={s.drawer}>
        <header style={s.header}>
          <h2 style={s.h2}>{t("home.drawer.title")}</h2>
          <button type="button" onClick={onClose} style={s.iconBtn} aria-label={t("home.drawer.close")}>✕</button>
        </header>

        <ol style={s.steps}>
          {steps.map((name, i) => (
            <li key={name} style={{ ...s.step, ...(i === stepIdx ? s.stepOn : null) }} aria-current={i === stepIdx ? "step" : undefined}>
              {i + 1}. {t(`home.drawer.steps.${name}`)}
            </li>
          ))}
        </ol>

        <div style={s.body}>
          {step === "source" && (
            <>
              <label style={s.label}>
                {t("home.drawer.edge")}
                <select
                  style={s.input}
                  value={selectedEdge?.id ?? ""}
                  onChange={(e) => onSelectEdge(edges.find((x) => x.id === e.target.value) ?? null)}
                >
                  <option value="">{t("home.drawer.selectEdge")}</option>
                  {edges.map((e) => (
                    <option key={e.id} value={e.id}>{e.name ?? e.edgeId}</option>
                  ))}
                </select>
              </label>
              <div style={s.label}>{t("home.drawer.whatToShow")}</div>
              <div style={s.cards} role="radiogroup" aria-label={t("home.drawer.steps.source")}>
                {SOURCES.map((o) => (
                  <button
                    key={o}
                    type="button"
                    role="radio"
                    aria-checked={source === o}
                    onClick={() => setSource(o)}
                    style={{ ...s.card, ...(source === o ? s.cardOn : null) }}
                  >
                    <strong>{t(`home.drawer.src.${o}.label`)}</strong>
                    <span style={s.hint}>{t(`home.drawer.src.${o}.hint`)}</span>
                  </button>
                ))}
              </div>
              {source !== "data" && <p style={s.note}>{t("home.drawer.noTagNote")}</p>}
            </>
          )}

          {step === "tag" && (
            <>
              <label style={s.label}>
                {t("home.drawer.devices")}
                <select style={{ ...s.input, height: 120 }} multiple value={deviceIds} onChange={(e) => setDeviceIds(Array.from(e.target.selectedOptions, (o) => o.value))}>
                  {devices.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </label>
              {devicesError && <span style={{ ...s.note, color: "var(--vx-danger)" }}>{devicesError}</span>}
              <label style={s.label}>
                {t("home.drawer.tags")}
                <select
                  style={{ ...s.input, height: 160 }}
                  multiple
                  value={tagNames}
                  disabled={deviceIds.length === 0}
                  onChange={(e) => setTagNames(Array.from(e.target.selectedOptions, (o) => o.value))}
                >
                  {tags.map((tg) => (
                    <option key={tg.id} value={tg.name}>{tg.label || tg.name}{tg.unit ? ` (${tg.unit})` : ""}</option>
                  ))}
                </select>
              </label>
            </>
          )}

          {step === "view" && (
            <div style={s.cards} role="radiogroup" aria-label={t("home.drawer.steps.view")}>
              {(source === "data" ? DATA_VIEWS : [source]).map((v) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={type === v}
                  onClick={() => source === "data" && setView(v)}
                  style={{ ...s.card, ...(type === v ? s.cardOn : null) }}
                >
                  <strong>{VIEWS[v].label}</strong>
                </button>
              ))}
            </div>
          )}

          {step === "settings" && (
            <>
              <label style={s.label}>
                {t("home.drawer.titleLabel")}
                <input style={s.input} value={title} placeholder={draft?.config?.title || ""} onChange={(e) => setTitle(e.target.value)} />
              </label>
              {(type === "linechart" || type === "table") && (
                <label style={s.label}>
                  {t("home.drawer.range")}
                  <select style={s.input} value={range} onChange={(e) => setRange(e.target.value)}>
                    <option value="15m">{t("home.drawer.range15m")}</option>
                    <option value="1h">{t("home.drawer.range1h")}</option>
                    <option value="24h">{t("home.drawer.range24h")}</option>
                  </select>
                </label>
              )}
              {type === "gauge" && (
                <>
                  <label style={{ ...s.label, flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <input type="checkbox" checked={autoRange} onChange={(e) => setAutoRange(e.target.checked)} />
                    {t("home.drawer.autoRange")}
                  </label>
                  {!autoRange && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <label style={{ ...s.label, flex: 1 }}>
                        {t("home.drawer.min")}
                        <input style={s.input} type="number" value={min} onChange={(e) => setMin(e.target.value)} />
                      </label>
                      <label style={{ ...s.label, flex: 1 }}>
                        {t("home.drawer.max")}
                        <input style={s.input} type="number" value={max} onChange={(e) => setMax(e.target.value)} />
                      </label>
                    </div>
                  )}
                  {rangeInvalid && <span style={{ ...s.note, color: "var(--vx-danger)" }}>{t("home.drawer.rangeInvalid")}</span>}
                </>
              )}
              {!["gauge", "linechart", "table"].includes(type) && <p style={s.note}>{t("home.drawer.noExtra")}</p>}
            </>
          )}
        </div>

        <section style={s.previewBox} aria-label={t("home.drawer.preview")}>
          <div style={s.previewHead}>{t("home.drawer.preview")}</div>
          <div style={s.previewCanvas}>
            {draft && previewHasData ? (
              <WidgetRenderer widget={{ ...draft, id: "preview" }} sensorData={{}} widgetData={previewWidgetData} onEventRangeChange={() => {}} />
            ) : (
              <span style={s.note}>
                {needsTag(type) ? t("home.drawer.previewNeedTags") : type === "edge_system" ? t("home.drawer.loading") : t("home.drawer.previewAfterAdd")}
              </span>
            )}
          </div>
        </section>

        <footer style={s.footer}>
          <button type="button" style={s.ghost} onClick={() => (stepIdx === 0 ? onClose() : setStepIdx(stepIdx - 1))}>
            {stepIdx === 0 ? t("home.drawer.cancel") : t("home.drawer.back")}
          </button>
          <div style={{ flex: 1 }} />
          {last ? (
            <button type="button" style={{ ...s.primary, opacity: canNext ? 1 : 0.5 }} disabled={!canNext} onClick={submit}>
              {t("home.drawer.add")}
            </button>
          ) : (
            <button type="button" style={{ ...s.primary, opacity: canNext ? 1 : 0.5 }} disabled={!canNext} onClick={() => setStepIdx(stepIdx + 1)}>
              {t("home.drawer.next")}
            </button>
          )}
        </footer>
      </aside>
    </div>
  );
}

const s = {
  scrim: { position: "fixed", inset: 0, background: "rgba(21, 26, 34, 0.35)", zIndex: 1000, display: "flex", justifyContent: "flex-end" },
  drawer: { width: "min(440px, 100vw)", height: "100%", background: "var(--vx-surface)", borderLeft: "1px solid var(--vx-line)", display: "flex", flexDirection: "column", fontFamily: "var(--vx-font-sans)", color: "var(--vx-text)", outline: "none" },
  header: { display: "flex", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid var(--vx-line)" },
  h2: { margin: 0, fontSize: 16, fontWeight: 700, flex: 1 },
  iconBtn: { border: 0, background: "none", fontSize: 16, cursor: "pointer", color: "var(--vx-text-2)", padding: 4 },
  steps: { display: "flex", gap: 4, listStyle: "none", margin: 0, padding: "10px 18px", borderBottom: "1px solid var(--vx-line-soft)", flexWrap: "wrap" },
  step: { fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 999, color: "var(--vx-text-2)" },
  stepOn: { background: "var(--vx-accent-soft)", color: "var(--vx-accent-strong)" },
  body: { padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12, overflowY: "auto", flex: 1, minHeight: 0 },
  label: { display: "flex", flexDirection: "column", gap: 6, fontSize: 13, fontWeight: 600 },
  input: { font: "inherit", fontWeight: 400, padding: "8px 10px", borderRadius: "var(--vx-radius-sm)", border: "1px solid var(--vx-line)", background: "var(--vx-surface)", color: "var(--vx-text)" },
  cards: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  card: { font: "inherit", textAlign: "left", display: "flex", flexDirection: "column", gap: 2, padding: "10px 12px", borderRadius: "var(--vx-radius-sm)", border: "1px solid var(--vx-line)", background: "var(--vx-surface)", color: "var(--vx-text)", cursor: "pointer" },
  cardOn: { borderColor: "var(--vx-accent)", background: "var(--vx-accent-soft)" },
  hint: { fontSize: 12, color: "var(--vx-text-2)" },
  note: { margin: 0, fontSize: 12, color: "var(--vx-text-2)" },
  previewBox: { borderTop: "1px solid var(--vx-line)", padding: "10px 18px", background: "var(--vx-bg)" },
  previewHead: { fontSize: 12, fontWeight: 600, color: "var(--vx-text-2)", marginBottom: 6 },
  previewCanvas: { height: 150, borderRadius: "var(--vx-radius)", border: "1px solid var(--vx-line)", background: "var(--vx-surface)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 },
  footer: { display: "flex", gap: 8, padding: "12px 18px", borderTop: "1px solid var(--vx-line)" },
  ghost: { font: "inherit", fontWeight: 600, padding: "8px 14px", borderRadius: "var(--vx-radius-sm)", border: "1px solid var(--vx-accent-line)", background: "var(--vx-surface)", color: "var(--vx-accent-strong)", cursor: "pointer" },
  primary: { font: "inherit", fontWeight: 600, padding: "8px 14px", borderRadius: "var(--vx-radius-sm)", border: 0, background: "var(--vx-accent)", color: "#ffffff", cursor: "pointer" },
};
